import type { Vec3, CraterEvent } from '../types/GameState.js';
import {
  TERRAIN_WIDTH, TERRAIN_DEPTH,
  TERRAIN_SEGMENTS_X, TERRAIN_SEGMENTS_Z,
  TERRAIN_MAX_HEIGHT,
  TANK_MAX_SLOPE_DEG, INFANTRY_MAX_SLOPE_DEG,
  SLOPE_SPEED_FALLOFF_START_DEG,
} from '../constants.js';

/**
 * Sample the terrain height at a given (x, z) world position
 * from the heightmap Float32Array.
 *
 * heightmap is stored row-major: index = z * (segmentsX + 1) + x
 * The terrain mesh spans from -TERRAIN_WIDTH/2 to +TERRAIN_WIDTH/2 on X,
 * and -TERRAIN_DEPTH/2 to +TERRAIN_DEPTH/2 on Z.
 */
export function sampleHeight(
  heightmap: Float32Array,
  worldX: number,
  worldZ: number
): number {
  const vertsX = TERRAIN_SEGMENTS_X + 1;
  const vertsZ = TERRAIN_SEGMENTS_Z + 1;

  // Convert world coords to UV (0..1)
  const u = (worldX + TERRAIN_WIDTH / 2) / TERRAIN_WIDTH;
  const v = (worldZ + TERRAIN_DEPTH / 2) / TERRAIN_DEPTH;

  // Clamp to valid range
  const cu = Math.max(0, Math.min(1, u));
  const cv = Math.max(0, Math.min(1, v));

  // Convert to grid coords
  const gx = cu * (vertsX - 1);
  const gz = cv * (vertsZ - 1);

  // Integer grid positions
  const ix = Math.floor(gx);
  const iz = Math.floor(gz);
  const fx = gx - ix;
  const fz = gz - iz;

  // Clamp indices
  const ix0 = Math.min(ix, vertsX - 1);
  const ix1 = Math.min(ix + 1, vertsX - 1);
  const iz0 = Math.min(iz, vertsZ - 1);
  const iz1 = Math.min(iz + 1, vertsZ - 1);

  // Bilinear interpolation
  const h00 = heightmap[iz0 * vertsX + ix0];
  const h10 = heightmap[iz0 * vertsX + ix1];
  const h01 = heightmap[iz1 * vertsX + ix0];
  const h11 = heightmap[iz1 * vertsX + ix1];

  const h0 = h00 + (h10 - h00) * fx;
  const h1 = h01 + (h11 - h01) * fx;

  return h0 + (h1 - h0) * fz;
}

/**
 * Compute the terrain normal at a given (x, z) position using finite differences.
 */
export function sampleNormal(
  heightmap: Float32Array,
  worldX: number,
  worldZ: number,
  epsilon: number = 0.5
): Vec3 {
  const hL = sampleHeight(heightmap, worldX - epsilon, worldZ);
  const hR = sampleHeight(heightmap, worldX + epsilon, worldZ);
  const hD = sampleHeight(heightmap, worldX, worldZ - epsilon);
  const hU = sampleHeight(heightmap, worldX, worldZ + epsilon);

  // Cross product of tangent vectors
  const nx = hL - hR;
  const ny = 2 * epsilon;
  const nz = hD - hU;
  const len = Math.sqrt(nx * nx + ny * ny + nz * nz);

  return { x: nx / len, y: ny / len, z: nz / len };
}

/**
 * Apply a crater to the heightmap at the given world position.
 * Modifies the heightmap Float32Array in place.
 *
 * If addDirt is true, raises terrain instead of lowering it (Dirt Mover weapon).
 *
 * Features:
 * - Quadratic falloff for the main deformation
 * - Raised rim ring around the crater (displaced dirt)
 * - Post-deformation smoothing pass to cap crater wall slopes at ~60°
 *
 * Returns the list of affected vertex indices (for partial geometry updates).
 */
export function applyCrater(
  heightmap: Float32Array,
  crater: CraterEvent
): number[] {
  const vertsX = TERRAIN_SEGMENTS_X + 1;
  const vertsZ = TERRAIN_SEGMENTS_Z + 1;
  const cellW = TERRAIN_WIDTH / TERRAIN_SEGMENTS_X;
  const cellD = TERRAIN_DEPTH / TERRAIN_SEGMENTS_Z;

  const cx = crater.center.x;
  const cz = crater.center.z;
  const radius = crater.radius;
  const depth = crater.depth;
  const sign = crater.addDirt ? 1 : -1;

  // Extend affected area slightly beyond radius for the rim
  const outerRadius = radius * 1.25;

  // Compute grid bounds affected (including rim area)
  const minGX = Math.max(0, Math.floor(((cx - outerRadius) + TERRAIN_WIDTH / 2) / cellW));
  const maxGX = Math.min(vertsX - 1, Math.ceil(((cx + outerRadius) + TERRAIN_WIDTH / 2) / cellW));
  const minGZ = Math.max(0, Math.floor(((cz - outerRadius) + TERRAIN_DEPTH / 2) / cellD));
  const maxGZ = Math.min(vertsZ - 1, Math.ceil(((cz + outerRadius) + TERRAIN_DEPTH / 2) / cellD));

  const affected: number[] = [];

  for (let gz = minGZ; gz <= maxGZ; gz++) {
    for (let gx = minGX; gx <= maxGX; gx++) {
      // Convert grid to world
      const wx = gx * cellW - TERRAIN_WIDTH / 2;
      const wz = gz * cellD - TERRAIN_DEPTH / 2;

      const dx = wx - cx;
      const dz = wz - cz;
      const dist = Math.sqrt(dx * dx + dz * dz);
      const idx = gz * vertsX + gx;

      if (dist < radius) {
        // Main crater deformation — quadratic falloff
        const factor = 1 - (dist / radius);
        const deform = depth * factor * factor;
        heightmap[idx] += sign * deform;
        affected.push(idx);
      } else if (dist < outerRadius && !crater.addDirt) {
        // Rim — raised ring around crater (only for craters, not dirt mounds)
        const rimNorm = (dist - radius) / (outerRadius - radius); // 0 at crater edge, 1 at outer
        const rimHeight = depth * 0.15 * Math.sin(rimNorm * Math.PI);
        heightmap[idx] += rimHeight;
        affected.push(idx);
      }

      // Clamp height
      if (dist < outerRadius) {
        heightmap[idx] = Math.max(-20, Math.min(TERRAIN_MAX_HEIGHT + 20, heightmap[idx]));
      }
    }
  }

  // Smoothing pass — cap maximum slope to ~60° within the crater area
  // 60° slope = rise/run of tan(60°) ≈ 1.73
  const maxSlope = 1.73;
  const smoothPasses = 2;
  for (let pass = 0; pass < smoothPasses; pass++) {
    for (let gz = minGZ + 1; gz < maxGZ; gz++) {
      for (let gx = minGX + 1; gx < maxGX; gx++) {
        const idx = gz * vertsX + gx;
        const h = heightmap[idx];

        // Check all 4 neighbors
        const hL = heightmap[idx - 1];
        const hR = heightmap[idx + 1];
        const hD = heightmap[(gz - 1) * vertsX + gx];
        const hU = heightmap[(gz + 1) * vertsX + gx];

        const maxDiffX = maxSlope * cellW;
        const maxDiffZ = maxSlope * cellD;

        let adjusted = h;
        // Prevent being too much lower than any neighbor
        adjusted = Math.max(adjusted, hL - maxDiffX);
        adjusted = Math.max(adjusted, hR - maxDiffX);
        adjusted = Math.max(adjusted, hD - maxDiffZ);
        adjusted = Math.max(adjusted, hU - maxDiffZ);
        // Prevent being too much higher than any neighbor
        adjusted = Math.min(adjusted, hL + maxDiffX);
        adjusted = Math.min(adjusted, hR + maxDiffX);
        adjusted = Math.min(adjusted, hD + maxDiffZ);
        adjusted = Math.min(adjusted, hU + maxDiffZ);

        if (adjusted !== h) {
          heightmap[idx] = adjusted;
        }
      }
    }
  }

  return affected;
}

// ─── Slope Helpers ───

/**
 * Get the slope angle in degrees at a world position.
 */
export function getSlopeAngle(
  heightmap: Float32Array,
  worldX: number,
  worldZ: number,
): number {
  const normal = sampleNormal(heightmap, worldX, worldZ);
  // normal.y = cos(slopeAngle), so angle = acos(y)
  const cosAngle = Math.max(0, Math.min(1, normal.y));
  return Math.acos(cosAngle) * (180 / Math.PI);
}

/**
 * Get the downhill direction (normalized XZ vector pointing downhill) at a world position.
 * Returns {x, z} or {x:0, z:0} if flat.
 */
export function getDownhillDirection(
  heightmap: Float32Array,
  worldX: number,
  worldZ: number,
): { x: number; z: number } {
  const normal = sampleNormal(heightmap, worldX, worldZ);
  // Project normal onto XZ plane — this points "uphill", so negate for downhill
  const dx = -normal.x;
  const dz = -normal.z;
  const len = Math.sqrt(dx * dx + dz * dz);
  if (len < 0.001) return { x: 0, z: 0 };
  return { x: dx / len, z: dz / len };
}

/**
 * Compute the speed multiplier based on terrain slope.
 *
 * @param slopeAngleDeg - current slope angle in degrees
 * @param maxSlopeDeg - max traversable slope (TANK_MAX_SLOPE_DEG or INFANTRY_MAX_SLOPE_DEG)
 * @returns 0..1 multiplier (0 = full stop, 1 = full speed)
 */
export function getSlopeSpeedMultiplier(
  slopeAngleDeg: number,
  maxSlopeDeg: number,
): number {
  if (slopeAngleDeg <= SLOPE_SPEED_FALLOFF_START_DEG) return 1;
  if (slopeAngleDeg >= maxSlopeDeg) return 0;

  // Linear falloff between start and max
  const t = (slopeAngleDeg - SLOPE_SPEED_FALLOFF_START_DEG) / (maxSlopeDeg - SLOPE_SPEED_FALLOFF_START_DEG);
  return 1 - t * 0.85; // minimum 15% speed at boundary
}

/**
 * Check if a position is within terrain bounds.
 */
export function isInBounds(worldX: number, worldZ: number): boolean {
  return (
    worldX >= -TERRAIN_WIDTH / 2 && worldX <= TERRAIN_WIDTH / 2 &&
    worldZ >= -TERRAIN_DEPTH / 2 && worldZ <= TERRAIN_DEPTH / 2
  );
}
